/**
 * User Detail Hook
 * Fetch detailed information for a single user
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import type { QuotaOverrides } from '@parlay-picker/shared/subscriptionTier';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface UserSubscription {
  id: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
}

export interface UserStats {
  parlays: number;
  legs: number;
}

export interface UserDetail {
  id: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  role: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  onboardingCompletedAt: string | null;
  subscription: UserSubscription | null;
  stats: UserStats;
  quotaOverrides: QuotaOverrides | null;
  tierDefaults: {
    standard: number;
    detailed: number;
    premium: number;
    analysis: number;
    tracking: number;
  };
}

export interface UseUserDetailResult {
  user: UserDetail | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useUserDetail(userId: string | null): UseUserDetailResult {
  const { authFetch } = useAuth();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!userId) {
      setUser(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(apiUrl(`/api/admin/users/${userId}`));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Unauthorized - admin access required');
        }
        if (response.status === 404) {
          throw new Error('User not found');
        }
        throw new Error('Failed to fetch user details');
      }

      const data = await response.json();
      setUser(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch, userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return {
    user,
    loading,
    error,
    refetch: fetchUser,
  };
}
